"use client";
/**
 * Top-Down shared state — universe selection, drill-through filters
 * (sector → industry → stock), the fetched payload, and a "loading"
 * flag every pane consumes. Centralised here so the four data panes
 * (Sectors / Industries / Stocks / Stock Detail) stay in sync without
 * each one re-fetching independently.
 *
 * The selected stock is also written to the symbol store under the
 * `top-down` scope, which is what the embedded Price Chart pane
 * subscribes to. Clicking a stock in the Stocks pane therefore charts
 * it on the same workspace.
 */
import { byUniverseName } from "@/lib/indices/universeOrder";
import type { SepaColumns } from "@/lib/minervini/scan";
import type { CandleColumns, WeeklyCandleColumns } from "@/lib/candles/columns";
import type { CycleColumns, WeeklyCycleColumns } from "@/lib/cycles/columns";
import type { StockbeeBarColumns } from "@/lib/stockbee/barColumns";
import type { StockbeeScanColumns } from "@/lib/stockbee/scanColumns";
import type { MmColumns } from "@/lib/stockbee/mmColumns";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { migrateLegacyCol } from "./dynamicIndicatorColumns";
import { PRESET_BY_ID, regenIds } from "./stockFilters";
import { SELECTABLE_UNIVERSES } from "@/lib/indices/universes";
import { GENERATED_LISTS } from "@/lib/indices/generated";
import { invalidateClientCache, useFetch } from "@/lib/useFetch";
import { decodeTopDownPayload } from "@/lib/scanner/topDownWire";
import { useClientEvent } from "@/lib/clientEvents";
import { useSymbolStore } from "@/lib/stores/symbolStore";
import { safeLocalStorageSet } from "@/lib/safeLocalStorage";

// Window keys + types — duplicated locally so this provider has no
// dependency on the panes (panes import from here, not the other way).
export const WINDOW_KEYS = [
  "rt", "d1", "d2", "d5", "d10",
  "m1", "m2", "m3", "m4", "m5", "m6", "m7", "m8", "m9", "m10", "m11", "m12",
] as const;
export type WindowKey = (typeof WINDOW_KEYS)[number];
export const WINDOW_LABEL: Record<WindowKey, string> = {
  rt: "RT", d1: "1D", d2: "2D", d5: "5D", d10: "10D",
  m1: "M1", m2: "M2", m3: "M3", m4: "M4", m5: "M5", m6: "M6",
  m7: "M7", m8: "M8", m9: "M9", m10: "M10", m11: "M11", m12: "M12",
};

export type Counts = { up: number; down: number; flat: number };
export type WindowMap = Record<WindowKey, number | null>;

export type SectorRow = {
  sector: string;
  count: number;
  windows: WindowMap;
  pace1D: number | null;
  pace2D: number | null;
  pace5D: number | null;
  delta_2D_1D: number | null;
  delta_5D_2D: number | null;
  delta_10D_5D: number | null;
  /** CUE Elite "fM" force index — equal-weighted mean of constituents'
   *  volume-neutralized force (100 × EMA(vol×Δclose,N)/EMA(vol,N)). */
  f2: number | null;
  f5: number | null;
  f13: number | null;
};
export type IndustryRow = SectorRow & { industry: string };
/** Catalog ETF — that fund's own ladder, same shape as a sector row. */
export type ThemeRow = {
  symbol: string;
  name: string;
  category: string;
  /** Cached US holdings in this ETF, same as Sectors' n. */
  count: number;
  windows: WindowMap;
  pace1D: number | null;
  pace2D: number | null;
  pace5D: number | null;
  delta_2D_1D: number | null;
  delta_5D_2D: number | null;
  delta_10D_5D: number | null;
  f2: number | null;
  f5: number | null;
  f13: number | null;
};
export type Fundamentals = {
  eps_growth_1y: number | null;
  eps_growth_2y: number | null;
  eps_growth_3y: number | null;
  eps_growth_4y: number | null;
  eps_growth_5y: number | null;
  rev_growth_1y: number | null;
  rev_growth_2y: number | null;
  rev_growth_3y: number | null;
  rev_growth_4y: number | null;
  rev_growth_5y: number | null;
  eps_yoy_q0: number | null;
  eps_yoy_q1: number | null;
  eps_yoy_q2: number | null;
  eps_yoy_q3: number | null;
  rev_yoy_q0: number | null;
  rev_yoy_q1: number | null;
  rev_yoy_q2: number | null;
  rev_yoy_q3: number | null;
  next_earnings_date: string | null;
  pe_ratio: number | null;
  forward_pe: number | null;
  price_to_book: number | null;
  price_to_sales: number | null;
  ev_to_ebitda: number | null;
  dividend_yield_pct: number | null;
  gross_margin_pct: number | null;
  op_margin_pct: number | null;
  net_margin_pct: number | null;
  return_on_equity_pct: number | null;
  debt_to_equity: number | null;
  /** How old the eleven TTM ratios above are (lib/fundamentals/ttmRatios.ts):
   *  the close FMP's nightly files are priced at (`file`), or the day the
   *  company was read alone (`company`). Absent from a snapshot the worker
   *  wrote before 2026-10-16. */
  ratios_asof?: string | null;
  ratios_from?: string | null;
  /** CUE Edge-style composite scores — sector-percentile rank 0..100.
   *  Computed by the FMP fundamentals refresh worker once per cycle. */
  score_valuation: number | null;
  score_quality: number | null;
  score_box: number | null;
  score_short_squeeze: number | null;
  /** IAN Nine Box data-field rating: 1..9, 0 = No Box, null = unknown. */
  box_class: number | null;
  /** Growth Boxes, this app's Rule #1 reading (lib/fundamentals/growthBoxes.ts),
   *  stored nightly: Growth Box 0 to 10, Pace percent a year, Consistency and
   *  Composite 0 to 100; null is not measured. Absent from a snapshot the
   *  worker wrote before 2026-11-03. */
  growth_box?: number | null;
  growth_boxes_measured?: number | null;
  growth_pace?: number | null;
  growth_years_to_double?: number | null;
  growth_consistency?: number | null;
  growth_composite?: number | null;
  growth_fy_end?: string | null;
  /** Analyst grades + 90-day insider rollup (null when not covered). */
  analyst_buy: number | null;
  analyst_hold: number | null;
  analyst_sell: number | null;
  analyst_target_mean: number | null;
  insider_net_usd: number | null;
  insider_buy_count: number | null;
  insider_sell_count: number | null;
  insider_cluster: number | null;
};
export type StockRow = {
  symbol: string;
  name: string | null;
  sector: string | null;
  industry: string | null;
  last: number | null;
  windows: WindowMap;
  /** Client-only extended-hours % (live SSE). Absent/null in batch payloads. */
  ext_pct?: number | null;
  pct_from_hi_52w: number | null;
  pct_from_lo_52w: number | null;
  vs_sma_50_pct: number | null;
  vs_sma_200_pct: number | null;
  rsi_14: number | null;
  vol_ma_20: number | null;
  /** CUE Elite "fM" force-index oscillators — volume-neutralized Elder Force
   *  Index: 100 × EMA(vol×Δclose, N) / EMA(vol, N) for N=2/5/13. */
  f2: number | null;
  f5: number | null;
  f13: number | null;
  /** Relative volume — latest vol as a percent of the prior 50-day average. */
  rvol: number | null;
  /** IBD-style RS Rank (1-99), percentile across the whole All-US-Stocks market
   *  (invariant to the selected universe). */
  rs_rank: number | null;
  /** Universe-relative RS Rank (1-99): the same score percentiled within the
   *  selected universe's members, so it changes per universe. */
  rs_rank_univ: number | null;
  rs9_rank: number | null;
  /** Earnings Lift, Industry Strength and Sector Strength, 0-100 within the selected universe. */
  earnings_lift: number | null;
  industry_strength: number | null;
  sector_strength: number | null;
  /** OBV Rank (1-99) — percentile of OBV net-accumulation across the universe. */
  obv_rank: number | null;
  /** Weekly RS — signed 5-session relative return vs SPY (Impulse view). */
  rs_w: number | null;
  /** Signed days/weeks above (+) / below (−) the moving average (Impulse view):
   *  OBV vs its EMA; daily/weekly RS (close/SPY) vs its RSMA. */
  obv_d_age: number | null;
  obv_w_age: number | null;
  rs_d_age: number | null;
  rs_w_age: number | null;
  rs_d_value: number | null; // raw daily RS ratio
  rs_nh: number | null; // RS line (close/SPY) at a new 52-week high (1/0)
  rs_rank_nh: number | null; // RS RANK (percentile) at a new 52-week high (1/0)
  rs_d_ma: number | null; // daily RSMA line
  rs_w_value: number | null; // raw weekly RS ratio
  rs_w_ma: number | null; // weekly RSMA line
  /** CUE Dots: Up / Down fire on the latest bar (1/0). */
  up_dots: number | null;
  down_dots: number | null;
  /** Signed bars since the daily / weekly Bongo regime flipped (+bull/−bear). */
  bongo_d_age: number | null;
  bongo_w_age: number | null;
  bongo_m_age: number | null;
  /** Signal scalars for the scan criteria / presets (latest completed bar). */
  sma_200: number | null;
  volume: number | null;
  bongo_d_state: number | null; // +1 bull / -1 bear / 0
  bongo_w_state: number | null;
  bongo_m_state: number | null;
  kf: number | null; // Kahuna Force dot: +1 / -1 / 0
  kf_run: number | null; // signed consecutive KF dots (2-day+ criteria)
  kf_age: number | null; // bars since the last Kahuna-Force fire
  bgu: number | null; gchrcl: number | null; mobo: number | null; pp: number | null; psar: number | null;
  bgu_age: number | null; gchrcl_age: number | null; mobo_age: number | null; pp_age: number | null; psar_age: number | null;
  ds_dir: number | null; // Dynamic Signal cross fire (+1 Buy / -1 Sell / 0)
  ds_age: number | null; // bars since the last Dynamic Signal cross fire
  ems_state: number | null; // +1 bull / -1 bear
  ems_cross: number | null; // +1 up-cross / -1 down-cross / 0
  ems_cross_w: number | null; // weekly EMS cross: +1 up / -1 down / 0
  force2: number | null; // close − EMA(2): >0 Force2 Up / <0 Down
  force13: number | null; // close − EMA(13): >0 Force13 Up / <0 Down
  kahuna_dpctb: number | null; // day-over-day Δ Bollinger %B (20,2)
  force2_w: number | null; // weekly close − EMA(2)
  force13_w: number | null; // weekly close − EMA(13)
  kahuna_dpctb_w: number | null; // week-over-week Δ Bollinger %B (20,2)
  force2_age: number | null; // bars the Force2 sign has held
  force2_w_age: number | null; // weeks the weekly Force2 sign has held
  force13_age: number | null; // bars the Force13 sign has held
  force13_w_age: number | null; // weeks the weekly Force13 sign has held
  big_kahuna_age: number | null; // bars since the last Big Kahuna fire
  big_kahuna_w_age: number | null; // weeks since the last weekly Big Kahuna fire
  small_kahuna_age: number | null; // bars since the last Small Kahuna fire
  small_kahuna_w_age: number | null; // weeks since the last weekly Small Kahuna fire
  sq_on: number | null; // squeeze ON (1/0)
  sq_rel: number | null; // squeeze release (1/0)
  sq_age: number | null; // bars the squeeze has been continuously ON
  rel_age: number | null; // bars the release window has been continuously ON
  cpr_d: number | null; // daily CPR trend / Daily Trend (+1 / -1)
  cpr_w: number | null; // weekly CPR trend / Weekly Trend
  rev_w: number | null; // weekly HCD(+1)/LCD(-1)
  // Standalone CPR — independent of the John Person columns.
  cpr_signal_d: number | null; // CPR daily flip dot (+1 / -1 / 0)
  cpr_state_d: number | null; // CPR daily regime (+1 / -1 / 0)
  cpr_state_w: number | null; // CPR weekly regime (+1 / -1 / 0)
  cpr_signal_w: number | null; // CPR weekly flip dot (+1 Buy / -1 Sell / 0)
  cpr_age: number | null; // bars since the last daily CPR flip
  cpr_w_age: number | null; // weeks since the last weekly CPR flip
  hcd_d_age: number | null; // bars since the last daily HCD fire
  hcd_w_age: number | null;
  hcd_m_age: number | null;
  lcd_d_age: number | null;
  lcd_w_age: number | null;
  lcd_m_age: number | null;
  ems_age: number | null; // bars the daily EMS state has held
  ems_state_w: number | null; // weekly EMS regime (+1 / -1 / 0)
  ems_w_age: number | null; // weeks the weekly EMS state has held
  // Person multi-timeframe criteria (signed; reuse jp_* person columns).
  signal_d: number | null;
  signal_w: number | null;
  signal_m: number | null;
  trend_m: number | null; // Monthly Trend
  hlcd_d: number | null; // Daily HCD(+1)/LCD(-1)
  hlcd_m: number | null; // Monthly HCD/LCD
  hist_d: number | null; // Daily JP pivot-momentum histogram
  hist_w: number | null; // Weekly JP pivot-momentum histogram
  hist_m: number | null; // Monthly JP pivot-momentum histogram
  vol_hist_d: number | null; // Daily Persons VM histogram
  vol_hist_w: number | null; // Weekly Persons VM histogram
  vol_hist_m: number | null; // Monthly Persons VM histogram
  lcc_d: number | null; // Daily real LCC trend (+1 Bull / -1 Bear / 0 Neutral)
  lcc_w: number | null; // Weekly real LCC trend
  lcc_m: number | null; // Monthly real LCC trend
  pivot_d: number | null; // Floor pivot per timeframe
  pivot_w: number | null;
  pivot_m: number | null;
  pivotma_d: number | null; // Pivot MA (PIPA = 3-period SMA)
  pivotma_w: number | null;
  pivotma_m: number | null;
  support_d: number | null; // S1
  support_w: number | null;
  support_m: number | null;
  distsup_d: number | null; // % from close to S1
  distsup_w: number | null;
  distsup_m: number | null;
  resist_d: number | null; // R1
  resist_w: number | null;
  resist_m: number | null;
  distres_d: number | null; // % from close to R1
  distres_w: number | null;
  distres_m: number | null;
  pmc_value_d: number | null;
  pmc_value_w: number | null;
  pmc_value_m: number | null;
  pmc_ma_d: number | null;
  pmc_ma_w: number | null;
  pmc_ma_m: number | null;
  atr: number | null;
  rng: number | null;
  chg: number | null;
  oc_rule_d: number | null;
  oc_rule_w: number | null;
  oc_rule_m: number | null;
  oc_sr_d: number | null;
  oc_sr_w: number | null;
  oc_sr_m: number | null;
  pstatus_d: number | null;
  pstatus_w: number | null;
  pstatus_m: number | null;
  pmc_stage_d: number | null;
  tech_rank: number | null;
  vol_delta: number | null; // today's volume − prior session
  // SMA 17/50/200 cross (+1 up / -1 down) + support/resistance (+1 / -1).
  sma17_cross: number | null;
  sma17_sr: number | null;
  sma50_cross: number | null;
  sma50_sr: number | null;
  sma200_cross: number | null;
  sma200_sr: number | null;
  sma17_sr_atr: number | null;
  sma17_sr_held: number | null;
  sma17_sr_age: number | null;
  sma17_reclaim: number | null;
  sma50_sr_atr: number | null;
  sma50_sr_held: number | null;
  sma50_sr_age: number | null;
  sma50_reclaim: number | null;
  sma200_sr_atr: number | null;
  sma200_sr_held: number | null;
  sma200_sr_age: number | null;
  sma200_reclaim: number | null;
  sma_17: number | null;
  sma_50: number | null;
  ma_stack: number | null;
  sma_confluence: number | null;
  ti: number | null;
  tl_support_break: number | null;
  tl_resist_break: number | null;
  tl_support_bounce: number | null;
  tl_resist_bounce: number | null;
  mem_pullback: number | null;
  mem_breakout: number | null;
  mem_bounce: number | null;
  mem_box: number | null;
  mem_touch: number | null;
  mem_reversal: number | null;
  mem_headwind: number | null;
  mem_flow: number | null;
  /** VPA (HGSI Volume Price Analysis) latest completed-bar scalars.
   *  sentiment/trend: +1 bull-up / -1 bear-down / 0. signal: VPA Signal fired
   *  (1/0). days_since: bars since the last VPA Signal. flag: primary flag id. */
  vpa_sentiment: number | null;
  vpa_signal: number | null;
  vpa_days_since: number | null;
  vpa_flag: string | null;
  vpa_trend_major: number | null;
  vpa_trend_minor: number | null;
  vpa_trend_immediate: number | null;
  /** Risk stats vs SPY over ~1yr daily (see lib/scanner/indicators.ts). */
  beta: number | null;
  alpha: number | null;
  vol_sd_30: number | null;
  ret_20d: number | null;
  ret_60d: number | null;
  bb_pctb: number | null;
  ret_5d_sigma: number | null;
  ret_20d_sigma: number | null;
  ret_60d_sigma: number | null;
  ret_20d_sector_sigma: number | null;
  /** Market cap in raw USD (FMP profile-bulk via symbol_meta). */
  market_cap: number | null;
  /** Short interest (FINRA). shares / days-to-cover / settlement date. */
  short_interest: number | null;
  short_dtc: number | null;
  short_settlement: string | null;
  fundamentals?: Fundamentals | null;
} & SepaColumns &
  CandleColumns &
  WeeklyCandleColumns &
  CycleColumns &
  WeeklyCycleColumns &
  StockbeeBarColumns &
  StockbeeScanColumns &
  MmColumns;
export type TopDownPayload = {
  asOf: number | null;
  computedAt: number;
  isLive: boolean;
  universeKey: string;
  universeLabel: string;
  universeCount: number;
  /** Roster members dropped by the instrument-type filter. */
  excludedByType?: number;
  /** Rows listed but out of every count and mean: not an operating company's
   *  common share (a SPAC's unit, warrant, right or share). Absent on a
   *  snapshot written before 2026-10-22. */
  excludedNonCommon?: number;
  /** How many issues an exchange's all-issues list holds, priced or not;
   *  only on one of those lists, whose `universeCount` is its stocks and ETFs
   *  (lib/scanner/topDown.ts). */
  listCount?: number;
  typesIncluded: string[];
  sectorAd: Record<WindowKey, Counts>;
  industryAd: Record<WindowKey, Counts>;
  /** Absent on snapshots written before Themes joined the worker pass. */
  themeAd?: Record<WindowKey, Counts>;
  sectors: SectorRow[];
  // industries arrive flattened with `{ industry, sector, ... }`
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  industries: any[];
  themes?: ThemeRow[];
  stocks: StockRow[];
};

// The universe dropdown binds to the single source of truth (SSOT):
// lib/indices/universes.ts with its names, and the exchanges' all-issues lists
// (README "The Scanner reads the one list"), in a picker's order
// (alphabetical, lib/indices/universeOrder.ts). Add a universe THERE, not here.
export const UNIVERSES: ReadonlyArray<{ slug: string; label: string }> = byUniverseName(
  [...SELECTABLE_UNIVERSES, ...GENERATED_LISTS.map((g) => ({ slug: g.slug, label: g.label }))],
  (u) => u.label,
);

const LS_UNIVERSE_KEY = "mpp:topDown:universe:v1";
// v1 was a single shared array; v2 is a per-view map { view: cols[] } so each
// Stocks-pane view keeps its own added columns. v1 is migrated into `standard`.
const LS_DYNCOLS_KEY = "mpp:scanner:dynamic-indicators:v1";
const LS_DYNCOLS_KEY_V2 = "mpp:scanner:dynamic-indicators:v2";
// The active Presets + criteria filter — persisted so it survives navigating
// away and back (the provider is mounted per-page, so it remounts on return).
const LS_STOCK_FILTERS_KEY = "mpp:scanner:stock-filters:v1";

/** Validate a raw value into a clean DynamicIndicatorCol[] (drops malformed),
 *  migrating any legacy per-timeframe indicator ids to the consolidated id + tf. */
function cleanDynCols(parsed: unknown): DynamicIndicatorCol[] {
  if (!Array.isArray(parsed)) return [];
  return parsed
    .filter(
      (c): c is DynamicIndicatorCol =>
        c && typeof c.colId === "string" && typeof c.indicatorId === "string" &&
        typeof c.label === "string" && c.params && typeof c.params === "object" &&
        (c.customLabel === undefined || typeof c.customLabel === "string"),
    )
    .map(migrateLegacyCol);
}

/** A custom stock-filter criterion (column + operator + value[s]).
 *  `col2`, when set, compares the column against ANOTHER column's value
 *  (e.g. Last ≥ SMA 200) instead of the numeric `value`. */
export type StockCriterion = {
  id: string;
  col: string;
  op: ">" | ">=" | "<" | "<=" | "==" | "between";
  value: number | null;
  value2?: number | null;
  col2?: string | null;
  /** The preset this criterion was loaded from (if any). Unchecking that preset
   *  removes its tagged criteria; manual criteria have no `presetId`. */
  presetId?: string | null;
  /** Per-criterion enable toggle (default on). A disabled criterion is ignored
   *  by the filter so you can A/B its impact without deleting it. */
  enabled?: boolean;
};

/** A nested boolean group in the filter tree — its `children` (leaf criteria or
 *  further groups) combined with AND / OR. The Stocks filter is a `FilterNode[]`
 *  combined at the top level with AND; a child group can switch to OR (and
 *  nest further), giving the full nested editor (AND inside OR inside AND …). */
export type FilterGroup = {
  id: string;
  kind: "group";
  op: "AND" | "OR";
  children: FilterNode[];
  presetId?: string | null;
  /** Per-group enable toggle (default on) — disabling skips the whole group. */
  enabled?: boolean;
};
export type FilterNode = StockCriterion | FilterGroup;

/** A user-added, configurable indicator column on the Stocks table. Its value
 *  is computed on demand (server-side) from `scanner_bars` via the shared
 *  indicator library — `indicatorId` keys into SHARED_INDICATOR_BY_ID, `params`
 *  overrides its defaults. `label` is the (editable) column header. */
export type DynamicIndicatorCol = {
  colId: string;
  indicatorId: string;
  params: Record<string, number>;
  /** Auto-generated label (from indicator + params). */
  label: string;
  /** User-overridden column header; falls back to `label` when unset/empty. */
  customLabel?: string;
};

type TopDownState = {
  universe: string;
  setUniverse: (u: string) => void;
  /** Multi-select sector / industry filters (checkbox sets). The Stocks
   *  pane shows the UNION of the checked rows. */
  sectorFilter: Set<string>;
  toggleSector: (s: string) => void;
  setSectorFilter: (s: Set<string>) => void;
  industryFilter: Set<string>;
  toggleIndustry: (s: string) => void;
  setIndustryFilter: (s: Set<string>) => void;
  /** Multi-select ETF theme tickers. Stocks shows the UNION of holdings. */
  themeFilter: Set<string>;
  toggleTheme: (s: string) => void;
  setThemeFilter: (s: Set<string> | ((prev: Set<string>) => Set<string>)) => void;
  /** Stocks-pane criteria filter: named presets + custom builder. */
  stockPresets: Set<string>;
  toggleStockPreset: (id: string) => void;
  setStockPresets: (s: Set<string>) => void;
  clearStockPresets: () => void;
  stockCriteria: FilterNode[];
  setStockCriteria: (c: FilterNode[]) => void;
  /** True after the persisted Presets + criteria have been read (or the
   *  default "A" scan applied). Recs views wait on this so they don't seed
   *  into an empty builder that the hydrate then overwrites. */
  filtersReady: boolean;
  /** User-added configurable indicator columns, keyed by Stocks-pane view
   *  (`standard` / `topDown` / `impulse`) so each view keeps its OWN added
   *  columns — adding one in Top Down doesn't leak into Impulse. Persisted,
   *  universe-independent (they recompute for whatever universe is active). */
  dynamicIndicatorColsByView: Record<string, DynamicIndicatorCol[]>;
  setDynamicIndicatorColsForView: (view: string, c: DynamicIndicatorCol[]) => void;
  selectedSymbol: string | null;
  /** Sets the row selection AND writes through to the symbol store so
   *  the embedded Price Chart pane re-anchors on this ticker. */
  selectSymbol: (s: string | null) => void;
  resetSelections: () => void;
  data: TopDownPayload | null;
  loading: boolean;
  error: unknown;
};

const Ctx = createContext<TopDownState | null>(null);

export function TopDownProvider({ children }: { children: ReactNode }) {
  const [universe, setUniverseRaw] = useState<string>("sp_1500");
  const [sectorFilter, setSectorFilter] = useState<Set<string>>(new Set());
  const [industryFilter, setIndustryFilter] = useState<Set<string>>(new Set());
  const [themeFilter, setThemeFilter] = useState<Set<string>>(new Set());
  const [stockPresets, setStockPresets] = useState<Set<string>>(new Set());
  const [stockCriteria, setStockCriteria] = useState<FilterNode[]>([]);
  const [filtersReady, setFiltersReady] = useState(false);
  const [dynamicIndicatorColsByView, setDynByViewRaw] = useState<Record<string, DynamicIndicatorCol[]>>({});
  const setSymbolInStore = useSymbolStore((s) => s.setSymbol);
  const storeSymbol = useSymbolStore((s) => s.symbol);
  const [selectedSymbol, setSelectedSymbolLocal] = useState<string | null>(
    () => storeSymbol || null,
  );
  // Mirror the page store into selectedSymbol so Stock Detail follows a
  // watchlist pick / More-link handoff / remount — not just table clicks.
  // One-way store→state; no loop, since setting selectedSymbol never writes
  // back to the store.
  const [refetchTick, setRefetchTick] = useState(0);

  // Hydrate / persist universe selection
  useEffect(() => {
    try {
      const stored = localStorage.getItem(LS_UNIVERSE_KEY);
      if (stored && UNIVERSES.some((u) => u.slug === stored)) {
        setUniverseRaw(stored);
      }
    } catch {}
  }, []);
  const setUniverse = useCallback((u: string) => {
    setUniverseRaw(u);
    safeLocalStorageSet(LS_UNIVERSE_KEY, u); // synced key — must fire the prefs-sync hook
  }, []);

  // The scanner's DEFAULT filter is the "A" scan (tagged so Clear/uncheck removes
  // it) — applied only on a BRAND-NEW profile (nothing persisted yet) so the pane
  // opens with a useful screen. Once the user picks a preset it's persisted and
  // restored on every remount (see the hydrate/persist effects below); Clear
  // still empties it and that empty state persists too.
  const applyDefaultCriteria = useCallback(() => {
    const a = PRESET_BY_ID["mpp_a"];
    if (a) {
      setStockPresets(new Set(["mpp_a"]));
      setStockCriteria(regenIds(a.criteria).map((n) => ({ ...n, presetId: "mpp_a" })));
    } else {
      setStockPresets(new Set());
      setStockCriteria([]);
    }
  }, []);

  // Hydrate the active Presets + criteria ONCE on mount. If the user has a
  // stored filter (from a prior session / before navigating away), restore it;
  // otherwise fall back to the default "A" scan. This is what makes a chosen
  // preset survive a page change — without it, the per-page provider remounted
  // and the universe effect below re-applied "A" every time.
  const filtersHydratedRef = useRef(false);
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_STOCK_FILTERS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as { presets?: unknown; criteria?: unknown };
        if (Array.isArray(parsed?.presets) && Array.isArray(parsed?.criteria)) {
          setStockPresets(new Set((parsed.presets as unknown[]).filter((x): x is string => typeof x === "string")));
          setStockCriteria(parsed.criteria as FilterNode[]);
          filtersHydratedRef.current = true;
          setFiltersReady(true);
          return;
        }
      }
    } catch {}
    applyDefaultCriteria();
    filtersHydratedRef.current = true;
    setFiltersReady(true);
  }, [applyDefaultCriteria]);

  // Persist the active Presets + criteria whenever they change (after the
  // initial hydration, so we never clobber the stored value with the default
  // before it's read). Empty is a valid persisted state (explicit Clear sticks).
  //
  // MUST go through safeLocalStorageSet, NOT a raw localStorage.setItem: this is
  // a synced (`mpp:*`) key, and only safeLocalStorageSet fires the prefs-sync
  // hook that mirrors the write to the server. With a raw write the server never
  // saw the change, and prefsSync's server-canonical hydratePrefs (which runs
  // before this provider mounts) wrote the STALE server copy back down on the
  // next page load — the same class of bug chartTemplates.ts documents. That is
  // exactly why a changed preset reverted on navigating away and back.
  useEffect(() => {
    if (!filtersHydratedRef.current) return;
    safeLocalStorageSet(
      LS_STOCK_FILTERS_KEY,
      JSON.stringify({ presets: [...stockPresets], criteria: stockCriteria }),
    );
  }, [stockPresets, stockCriteria]);

  // Hydrate / persist the user's dynamic indicator columns. These are
  // universe-INDEPENDENT (kept across universe switches; they recompute for the
  // active universe's symbols), so they live in their own key, not in the
  // universe-reset block below.
  useEffect(() => {
    try {
      // Prefer the per-view map (v2).
      const rawV2 = localStorage.getItem(LS_DYNCOLS_KEY_V2);
      if (rawV2) {
        const parsed = JSON.parse(rawV2);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          const map: Record<string, DynamicIndicatorCol[]> = {};
          for (const k of Object.keys(parsed)) map[k] = cleanDynCols(parsed[k]);
          setDynByViewRaw(map);
          return;
        }
      }
      // Migrate the old single shared array (v1) into the `standard` view.
      const rawV1 = localStorage.getItem(LS_DYNCOLS_KEY);
      if (rawV1) {
        const clean = cleanDynCols(JSON.parse(rawV1));
        if (clean.length) setDynByViewRaw({ standard: clean });
      }
    } catch {}
  }, []);
  const setDynamicIndicatorColsForView = useCallback(
    (view: string, c: DynamicIndicatorCol[]) => {
      setDynByViewRaw((prev) => {
        const next = { ...prev, [view]: c };
        safeLocalStorageSet(LS_DYNCOLS_KEY_V2, JSON.stringify(next)); // synced key — must fire the prefs-sync hook
        return next;
      });
    },
    [],
  );

  const url = useMemo(
    () => `/api/timing/top-down?universe=${universe}`,
    [universe],
  );
  const { data: wire, error, loading } = useFetch<TopDownPayload>(
    url,
    [refetchTick, url],
    { refreshMs: 30_000, workerSnapshot: true },
  );
  // Fundamentals are a third of the payload and change daily, while the grid's
  // ETag moves every ~72s — so they are their own resource, fetched on a slow
  // cadence and merged below. `refreshWhen: "always"` because fundamentals go
  // stale on the overnight update, not on the tape.
  const { data: fundamentals } = useFetch<Record<string, unknown>>(
    "/api/timing/top-down/fundamentals",
    [],
    { refreshMs: 15 * 60_000, refreshWhen: "always" },
  );

  // `stocks` arrives columnar (names once, then positional rows). Decoded once
  // here so every pane below still sees a plain StockRow[] — fundamentals and
  // all.
  const data = useMemo(
    () => decodeTopDownPayload(wire, fundamentals),
    [wire, fundamentals],
  );

  // Refetch when an upload changes the universe.
  const onIndexChange = useCallback(() => {
    invalidateClientCache(url);
    setRefetchTick((n) => n + 1);
  }, [url]);
  useClientEvent("index-uploaded", onIndexChange);

  // Reset the sector → industry → symbol DRILL when the universe genuinely
  // changes (skip the initial mount — hydration already set the filter). The
  // Presets + criteria scan is intentionally kept: it's persisted and applies
  // to the new universe's rows, so switching universes no longer wipes it back
  // to "A".
  const universeInitRef = useRef(true);
  useEffect(() => {
    if (universeInitRef.current) {
      universeInitRef.current = false;
      return;
    }
    setSectorFilter(new Set());
    setIndustryFilter(new Set());
    setThemeFilter(new Set());
    setSelectedSymbolLocal(null);
  }, [universe]);

  const resetSelections = useCallback(() => {
    setSectorFilter(new Set());
    setIndustryFilter(new Set());
    setThemeFilter(new Set());
    setStockPresets(new Set());
    setStockCriteria([]);
    setSelectedSymbolLocal(null);
  }, []);

  const toggleSector = useCallback((s: string) => {
    setSectorFilter((prev) => {
      const next = new Set(prev);
      next.has(s) ? next.delete(s) : next.add(s);
      return next;
    });
    // Picking sectors anew shouldn't keep stale industry checks around.
    setIndustryFilter(new Set());
  }, []);
  const toggleIndustry = useCallback((s: string) => {
    setIndustryFilter((prev) => {
      const next = new Set(prev);
      next.has(s) ? next.delete(s) : next.add(s);
      return next;
    });
  }, []);
  const toggleTheme = useCallback((s: string) => {
    setThemeFilter((prev) => {
      const next = new Set(prev);
      next.has(s) ? next.delete(s) : next.add(s);
      return next;
    });
  }, []);
  const toggleStockPreset = useCallback((id: string) => {
    setStockPresets((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);
  const clearStockPresets = useCallback(() => setStockPresets(new Set()), []);

  // Wrap symbol-selection so it also writes to the symbol store. The
  // Price Chart pane subscribes to the store; this is what makes
  // "click a stock in the Stocks pane" instantly chart the symbol.
  const selectSymbol = useCallback((sym: string | null) => {
    setSelectedSymbolLocal(sym);
    if (sym) setSymbolInStore(sym);
  }, [setSymbolInStore]);

  // Follow the chart: when the `top-down` store symbol changes (e.g. the user
  // types a ticker directly into the chart), re-anchor Stock Detail on it. Keyed
  // on the store symbol alone (functional set avoids a redundant render when it
  // already matches), so a universe-change reset to null stays null until the
  // chart symbol actually moves again.
  useEffect(() => {
    if (!storeSymbol) return;
    setSelectedSymbolLocal((prev) => (prev === storeSymbol ? prev : storeSymbol));
  }, [storeSymbol]);

  const value = useMemo<TopDownState>(() => ({
    universe,
    setUniverse,
    sectorFilter,
    toggleSector,
    setSectorFilter,
    industryFilter,
    toggleIndustry,
    setIndustryFilter,
    themeFilter,
    toggleTheme,
    setThemeFilter,
    stockPresets,
    toggleStockPreset,
    setStockPresets,
    clearStockPresets,
    stockCriteria,
    setStockCriteria,
    filtersReady,
    dynamicIndicatorColsByView,
    setDynamicIndicatorColsForView,
    selectedSymbol,
    selectSymbol,
    resetSelections,
    data: data ?? null,
    loading,
    error,
  }), [universe, setUniverse, sectorFilter, toggleSector, industryFilter, toggleIndustry, themeFilter, toggleTheme, stockPresets, toggleStockPreset, setStockPresets, clearStockPresets, stockCriteria, filtersReady, dynamicIndicatorColsByView, setDynamicIndicatorColsForView, selectedSymbol, selectSymbol, resetSelections, data, loading, error]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTopDown(): TopDownState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useTopDown must be used inside <TopDownProvider>");
  return v;
}

// ── Heatmap helpers shared by every pane ─────────────────────────────
// Red bearish, green bullish — colors track CUE Edge convention,
// saturation maxes out at ±5%.
export function heatBg(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "transparent";
  const sat = Math.min(1, Math.abs(v) / 5);
  const alpha = 0.08 + 0.45 * sat;
  return v >= 0
    ? `rgba(16, 185, 129, ${alpha})`
    : `rgba(239, 68, 68, ${alpha})`;
}
export function heatFg(v: number | null | undefined): string {
  // The heat tint sits on `bg-surface`, which flips with the theme, so the
  // readable ink flips too: dark text on the light-tinted cell in light mode,
  // light text on the dark-tinted cell in dark mode. Read the live theme off
  // <html> (the panes re-render on data ticks / navigation, so this tracks).
  const dark =
    typeof document !== "undefined" &&
    document.documentElement.classList.contains("dark");
  if (v == null || !Number.isFinite(v)) return "rgb(var(--mpp-ink-muted))";
  const sat = Math.abs(v) / 5;
  if (sat < 0.4) return dark ? "rgb(226 232 240)" : "rgb(15 23 42)";
  if (v >= 0) return dark ? "rgb(187 247 208)" : "rgb(6 78 59)";
  return dark ? "rgb(254 202 202)" : "rgb(127 29 29)";
}
// ── Discrete-state cell fills (CUE columns) ──────────────────────────
// Filled background + readable foreground for state / fire / quad cells,
// matching the CUE Edge scanner look (solid-color cells, not a text tint).
//
// PMC's 4 quadrants (DiffROC sign × DiffROC-vs-PMC-line):
//   1 above 0, strengthening — bright green · 2 above 0, weakening — dark blue
//   3 below 0, improving — magenta · 4 below 0, lagging — bright red
const QUAD_FILL: Record<number, { bg: string; fg: string }> = {
  1: { bg: "#22C55E", fg: "#06210F" },
  2: { bg: "#1D4ED8", fg: "#FFFFFF" },
  3: { bg: "#C026D3", fg: "#FFFFFF" },
  4: { bg: "#EF4444", fg: "#FFFFFF" },
};
/** Background for a 1–4 quadrant value (PMC); transparent otherwise. */
export function quadBg(v: number | null | undefined): string {
  return (v != null && QUAD_FILL[v]?.bg) || "transparent";
}
export function quadFg(v: number | null | undefined): string {
  return (v != null && QUAD_FILL[v]?.fg) || "rgb(148 163 184)";
}
/** Background for a signed state / fire value: green ≥0-pos, red neg, none on 0. */
export function stateBg(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v) || v === 0) return "transparent";
  return v > 0 ? "rgba(16, 185, 129, 0.85)" : "rgba(220, 38, 38, 0.85)";
}
export function stateFg(v: number | null | undefined): string {
  return v != null && Number.isFinite(v) && v !== 0 ? "#FFFFFF" : "rgb(148 163 184)";
}
/** Readable text color (dark or white) for an arbitrary hex background, by
 *  perceived luminance — used for per-def cellColors (e.g. yellow Squeeze). */
export function readableFg(hex: string): string {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6 ? "#15210A" : "#FFFFFF";
}

export function fmtPct(v: number | null | undefined, digits = 1): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toFixed(digits);
}
export function fmtAdHeader(c: Counts): string {
  return `${c.up}-${c.down}`;
}
